import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  ConnectionSettings,
  CopilotStudioClient,
  CopilotStudioWebChat,
  CopilotStudioWebChatConnection,
} from "@microsoft/agents-copilotstudio-client";
import { agentRequest } from "../auth/authConfig";

export type AgentState = {
  /** Direct Line-compatible connection for botframework-webchat. */
  directLine: CopilotStudioWebChatConnection | null;
  loading: boolean;
  error: string | null;
  /** Reset the conversation (e.g. when user clicks "New chat"). */
  reset: () => void;
};

const AGENT_URL = import.meta.env.VITE_COPILOT_AGENT_URL as string | undefined;

export function isAgentConfigured() {
  return Boolean(AGENT_URL && AGENT_URL.startsWith("https://"));
}

export function useAgent(active: boolean): AgentState {
  const { instance, accounts } = useMsal();
  const [directLine, setDirectLine] =
    useState<CopilotStudioWebChatConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetCount, setResetCount] = useState(0);
  const cancelledRef = useRef(false);

  const account = accounts[0];

  const acquireToken = useMemo(
    () => async () => {
      if (!account) throw new Error("No signed-in account");
      const result = await instance.acquireTokenSilent({
        ...agentRequest,
        account,
      });
      return result.accessToken;
    },
    [instance, account]
  );

  useEffect(() => {
    if (!active) return;
    if (!isAgentConfigured()) {
      setError("Copilot Studio agent is not configured.");
      return;
    }
    if (!account) {
      setError("Sign in required to start a conversation.");
      return;
    }

    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const token = await acquireToken();
        const settings: ConnectionSettings = {
          appClientId: import.meta.env.VITE_AAD_CLIENT_ID,
          tenantId: import.meta.env.VITE_AAD_TENANT_ID,
          environmentId: "",
          agentIdentifier: "",
          directConnectUrl: AGENT_URL!,
        };
        const client = new CopilotStudioClient(settings, token);
        const connection = CopilotStudioWebChat.createConnection(client, {
          showTyping: true,
        });
        if (cancelledRef.current) return;
        setDirectLine(connection);
      } catch (e: unknown) {
        if (cancelledRef.current) return;
        const message =
          e instanceof Error ? e.message : "Failed to start the conversation.";
        setError(message);
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [active, account, acquireToken, resetCount]);

  const reset = useCallback(() => {
    setDirectLine(null);
    setResetCount((n) => n + 1);
  }, []);

  return { directLine, loading, error, reset };
}
